// React widget registry. Widget packages register their components here.

import type { ComponentType } from "react";
import type { WidgetKind, Bindable } from "@viz/types";

export interface WidgetComponentProps {
  kind: WidgetKind;
  /** Resolved props (binds already evaluated). */
  props: Record<string, unknown>;
  children?: React.ReactNode;
  onDrill?: (entityId: string, opts?: { presentation?: string; ref?: string }) => void;
}

export type WidgetComponent = ComponentType<WidgetComponentProps>;

const registry = new Map<WidgetKind, WidgetComponent>();

export function registerWidget(kind: WidgetKind, comp: WidgetComponent): void {
  registry.set(kind, comp);
}

export function getWidget(kind: WidgetKind): WidgetComponent | undefined {
  return registry.get(kind);
}

export function listWidgets(): WidgetKind[] {
  return Array.from(registry.keys());
}

export type ResolvedProps = Record<string, unknown>;
export type Binder = (expr: string | { expr: string; fallback?: unknown } | unknown) => unknown;

export function resolveProps(
  raw: Record<string, Bindable<unknown>> | undefined,
  bind: Binder,
): ResolvedProps {
  if (!raw) return {};
  const out: ResolvedProps = {};
  for (const [k, v] of Object.entries(raw)) {
    out[k] = isBindExpr(v) ? bind(v) : v;
  }
  return out;
}

function isBindExpr(v: unknown): v is { expr: string; fallback?: unknown } {
  return typeof v === "object" && v != null && typeof (v as { expr?: unknown }).expr === "string";
}
